import { Download, File as FileIcon } from 'lucide-react';
import { type ImgHTMLAttributes, memo, useEffect, useState } from 'react';
import { type Components } from 'react-markdown';
import useSWR from 'swr';

import { type MessageUploadAttachment } from '@apps/web/components/phoebe_chat/transcript_types';
import { Markdown } from '@apps/web/components/ui/markdown';
import { Spinner } from '@apps/web/components/ui/spinner';
import { getApiClient } from '@apps/web/lib/api_client';
import { triggerBlobDownload } from '@apps/web/lib/tabular_export';
import { cn } from '@apps/web/lib/utils';
import { type Mode } from '@libraries/typescript/api_client';

export type V3PresentationContext = {
    readonly organizationId: string;
    readonly organizationTimezone: string;
    readonly mode: Mode;
};

export type V3ChatAttachment = {
    readonly kind: 'chat-attachment';
    readonly version: 1;
    readonly runId: string;
    readonly attachmentId: string;
    readonly filepath: string;
    readonly filename: string;
    readonly contentType: string;
    readonly disposition: 'inline' | 'attachment';
    readonly sizeBytes: number;
};

function formatAttachmentSize(sizeBytes: number): string {
    if (sizeBytes < 1024) {
        return `${sizeBytes} B`;
    }
    if (sizeBytes < 1024 * 1024) {
        const kilobytes = sizeBytes / 1024;
        return `${kilobytes.toFixed(kilobytes >= 10 ? 0 : 1)} KB`;
    }
    const megabytes = sizeBytes / (1024 * 1024);
    return `${megabytes.toFixed(megabytes >= 10 ? 0 : 1)} MB`;
}

function isImageAttachment(attachment: V3ChatAttachment): boolean {
    return (
        attachment.disposition === 'inline' &&
        attachment.contentType.toLowerCase().startsWith('image/')
    );
}

function isMarkdownAttachment(attachment: V3ChatAttachment): boolean {
    if (attachment.disposition !== 'inline') {
        return false;
    }
    const contentType = attachment.contentType.toLowerCase();
    return (
        contentType === 'text/markdown' ||
        contentType.startsWith('text/markdown;')
    );
}

const ATTACHMENT_URL_TEMPLATE =
    '/organizations/{organization_id}/phoebe_agent_runs/{phoebe_agent_run_id}/attachments/{attachment_id}' as const;

async function fetchAttachmentResponse({
    organizationId,
    runId,
    attachmentId,
    download,
}: {
    readonly organizationId: string;
    readonly runId: string;
    readonly attachmentId: string;
    readonly download: boolean;
}) {
    // Go through the api client so `X-Mode` is injected from the mode
    // store (the download endpoint fails closed on missing mode) and
    // credentials/auth headers are attached the same way as every other
    // API call. Hand-rolled `fetch(url)` would strip both.
    return getApiClient().raw<typeof ATTACHMENT_URL_TEMPLATE, 'get'>({
        method: 'GET',
        url: ATTACHMENT_URL_TEMPLATE,
        pathParams: {
            organization_id: organizationId,
            phoebe_agent_run_id: runId,
            attachment_id: attachmentId,
        },
        queryParams: download ? { download: true } : {},
        reason: 'v3-chat-attachment',
    });
}

async function fetchAttachmentBlobUrl(args: {
    readonly organizationId: string;
    readonly runId: string;
    readonly attachmentId: string;
}): Promise<string> {
    const response = await fetchAttachmentResponse({
        ...args,
        download: false,
    });
    if (!response.ok) {
        throw new Error(`Attachment fetch failed with ${response.status}`);
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
}

// Cap the inline markdown fetch at the same ceiling the sandbox uses so a
// pathological file cannot pull megabytes of prose into the transcript.
const MAX_INLINE_MARKDOWN_BYTES = 512 * 1024;

async function fetchAttachmentMarkdown(args: {
    readonly organizationId: string;
    readonly runId: string;
    readonly attachmentId: string;
}): Promise<string> {
    const response = await fetchAttachmentResponse({
        ...args,
        download: false,
    });
    if (!response.ok) {
        throw new Error(`Attachment fetch failed with ${response.status}`);
    }
    const text = await response.text();
    if (text.length > MAX_INLINE_MARKDOWN_BYTES) {
        return `${text.slice(0, MAX_INLINE_MARKDOWN_BYTES)}\n\n_Preview truncated — download the full file to see the rest._`;
    }
    return text;
}

// Blob-based download: fetch the file through the api client so `X-Mode`
// (and every other auth header the api client injects) rides along, then
// hand the bytes to the shared `triggerBlobDownload` from
// `apps/web/lib/tabular_export` — that helper owns the Arc/Chromium-safe
// dispatch (a real MouseEvent instead of `.click()`) AND the delayed
// `URL.revokeObjectURL` so Chromium/Arc actually get to fetch the blob
// before it's freed. A plain `<a href download>` cannot set headers, so
// navigating to the download URL directly would omit `X-Mode` and the
// endpoint would reject the request with 422 (see
// attachments.py::require_mode_context_from_header).
function DownloadButton({
    organizationId,
    runId,
    attachmentId,
    filename,
    className,
    children,
}: {
    readonly organizationId: string;
    readonly runId: string;
    readonly attachmentId: string;
    readonly filename: string;
    readonly className?: string;
    readonly children: React.ReactNode;
}) {
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState<string | null>(null);
    return (
        <button
            type="button"
            disabled={isDownloading}
            onClick={async () => {
                setDownloadError(null);
                setIsDownloading(true);
                try {
                    const response = await fetchAttachmentResponse({
                        organizationId,
                        runId,
                        attachmentId,
                        download: true,
                    });
                    if (!response.ok) {
                        throw new Error(
                            `Download failed with ${response.status}`,
                        );
                    }
                    const blob = await response.blob();
                    triggerBlobDownload(blob, filename);
                } catch (error) {
                    setDownloadError(
                        error instanceof Error
                            ? error.message
                            : 'Download failed',
                    );
                } finally {
                    setIsDownloading(false);
                }
            }}
            aria-label={
                downloadError ? `Download failed: ${downloadError}` : undefined
            }
            className={cn(
                'inline-flex items-center gap-1 text-[13px] leading-4 font-medium text-[#5533EE] hover:text-[#4629C7] disabled:cursor-progress disabled:opacity-70',
                className,
            )}
        >
            {children}
        </button>
    );
}

function ChatAttachmentFileCard({
    attachment,
    organizationId,
}: {
    readonly attachment: V3ChatAttachment;
    readonly organizationId: string;
}) {
    return (
        <div
            data-testid="chat-attachment-file-card"
            className="flex max-w-[520px] items-center gap-3 rounded-2xl border border-[#DBE3E3] bg-[#F0F5F5] px-4 py-3"
        >
            <span
                aria-hidden="true"
                className="flex size-9 shrink-0 items-center justify-center rounded-full bg-white text-[#617578] [&>svg]:size-4"
            >
                <FileIcon />
            </span>
            <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-[#002429]">
                    {attachment.filename}
                </div>
                <div className="text-xs text-[#617578]">
                    {formatAttachmentSize(attachment.sizeBytes)}
                </div>
            </div>
            <DownloadButton
                organizationId={organizationId}
                runId={attachment.runId}
                attachmentId={attachment.attachmentId}
                filename={attachment.filename}
            >
                <Download aria-hidden="true" className="size-4" />
                <span>Download</span>
            </DownloadButton>
        </div>
    );
}

function ChatAttachmentImageCard({
    attachment,
    organizationId,
}: {
    readonly attachment: V3ChatAttachment;
    readonly organizationId: string;
}) {
    // Fetch the image bytes through the api client (X-Mode + cookies) and
    // hand the <img> a blob URL. A cross-origin `<img src>` request would
    // otherwise be missing the mode header and the endpoint would fail
    // closed with 422.
    const { data: blobUrl, error } = useSWR<string>(
        [
            'chat-attachment-image',
            organizationId,
            attachment.runId,
            attachment.attachmentId,
        ],
        () =>
            fetchAttachmentBlobUrl({
                organizationId,
                runId: attachment.runId,
                attachmentId: attachment.attachmentId,
            }),
        {
            revalidateOnFocus: false,
            shouldRetryOnError: false,
        },
    );
    useEffect(() => {
        return () => {
            if (blobUrl) {
                URL.revokeObjectURL(blobUrl);
            }
        };
    }, [blobUrl]);
    if (error) {
        return (
            <ChatAttachmentFileCard
                attachment={attachment}
                organizationId={organizationId}
            />
        );
    }
    return (
        <div
            data-testid="chat-attachment-image-card"
            className="flex max-w-[520px] flex-col gap-2"
        >
            {blobUrl ? (
                <img
                    src={blobUrl}
                    alt={attachment.filename}
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    className="max-h-[420px] w-auto max-w-full rounded-2xl border border-[#DBE3E3] object-contain"
                />
            ) : (
                <div className="flex h-32 items-center justify-center rounded-2xl border border-[#DBE3E3] bg-[#F0F5F5] text-xs text-[#617578]">
                    Loading {attachment.filename}…
                </div>
            )}
            <div className="flex items-center justify-between gap-3 text-xs text-[#617578]">
                <span className="truncate">
                    {attachment.filename} &middot;{' '}
                    {formatAttachmentSize(attachment.sizeBytes)}
                </span>
                <DownloadButton
                    organizationId={organizationId}
                    runId={attachment.runId}
                    attachmentId={attachment.attachmentId}
                    filename={attachment.filename}
                    className="shrink-0"
                >
                    <Download aria-hidden="true" className="size-3.5" />
                    <span>Download</span>
                </DownloadButton>
            </div>
        </div>
    );
}

// Hardened <img> renderer for agent-attached markdown: only inline data URIs
// (embedded PNG/JPEG etc.) render. Every remote URL turns into a plain-text
// placeholder so opening the file cannot leak the viewer's IP to an arbitrary
// origin (react-markdown does not sanitize the img src attribute on its own).
const ATTACHMENT_MARKDOWN_COMPONENTS: Components = {
    img: ({ src, alt }: ImgHTMLAttributes<HTMLImageElement>) => {
        if (typeof src === 'string' && src.startsWith('data:')) {
            return (
                <img
                    src={src}
                    alt={alt}
                    referrerPolicy="no-referrer"
                    loading="lazy"
                    className="my-2 h-auto max-h-96 max-w-[80%] rounded-md border"
                />
            );
        }
        return (
            <span className="text-[#617578] italic">
                [image omitted: {alt || 'remote image blocked'}]
            </span>
        );
    },
};

function ChatAttachmentMarkdownCard({
    attachment,
    organizationId,
}: {
    readonly attachment: V3ChatAttachment;
    readonly organizationId: string;
}) {
    const { data, error, isLoading } = useSWR<string>(
        [
            'chat-attachment-markdown',
            organizationId,
            attachment.runId,
            attachment.attachmentId,
        ],
        () =>
            fetchAttachmentMarkdown({
                organizationId,
                runId: attachment.runId,
                attachmentId: attachment.attachmentId,
            }),
        {
            revalidateOnFocus: false,
            shouldRetryOnError: false,
        },
    );
    if (error) {
        return (
            <ChatAttachmentFileCard
                attachment={attachment}
                organizationId={organizationId}
            />
        );
    }
    return (
        <div
            data-testid="chat-attachment-markdown-card"
            className="flex max-w-[620px] flex-col gap-2 rounded-2xl border border-[#DBE3E3] bg-white px-4 py-3"
        >
            {isLoading || data === undefined ? (
                <div className="flex items-center gap-2 text-xs text-[#617578]">
                    <Spinner size="sm" />
                    <span>Loading {attachment.filename}…</span>
                </div>
            ) : (
                <Markdown
                    components={ATTACHMENT_MARKDOWN_COMPONENTS}
                    skipHtml={true}
                >
                    {data}
                </Markdown>
            )}
            <div className="flex items-center justify-between gap-3 border-t border-[#DBE3E3] pt-2 text-xs text-[#617578]">
                <span className="truncate">
                    {attachment.filename} &middot;{' '}
                    {formatAttachmentSize(attachment.sizeBytes)}
                </span>
                <DownloadButton
                    organizationId={organizationId}
                    runId={attachment.runId}
                    attachmentId={attachment.attachmentId}
                    filename={attachment.filename}
                    className="shrink-0"
                >
                    <Download aria-hidden="true" className="size-3.5" />
                    <span>Download</span>
                </DownloadButton>
            </div>
        </div>
    );
}

export const V3ChatAttachmentCard = memo(function V3ChatAttachmentCard({
    attachment,
    context,
}: {
    readonly attachment: V3ChatAttachment;
    readonly context: V3PresentationContext;
}) {
    if (isImageAttachment(attachment)) {
        return (
            <ChatAttachmentImageCard
                attachment={attachment}
                organizationId={context.organizationId}
            />
        );
    }
    if (isMarkdownAttachment(attachment)) {
        return (
            <ChatAttachmentMarkdownCard
                attachment={attachment}
                organizationId={context.organizationId}
            />
        );
    }
    return (
        <ChatAttachmentFileCard
            attachment={attachment}
            organizationId={context.organizationId}
        />
    );
});

export const V3UserUploadAttachmentCard = memo(
    function V3UserUploadAttachmentCard({
        upload,
        context,
    }: {
        readonly upload: MessageUploadAttachment;
        readonly context: V3PresentationContext;
    }) {
        return (
            <V3ChatAttachmentCard
                attachment={{
                    kind: 'chat-attachment',
                    version: 1,
                    runId: upload.runId,
                    attachmentId: upload.attachmentId,
                    filepath: upload.filename,
                    filename: upload.filename,
                    contentType: upload.contentType,
                    disposition: upload.disposition,
                    sizeBytes: upload.sizeBytes,
                }}
                context={context}
            />
        );
    },
);
